import React from 'react';
import { Bell, X, Calendar, BookOpen, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Push Notification Consent Modal
 * UK GDPR compliant: Clear explanation of what notifications will be used for
 * Triggered after meaningful engagement (first lesson OR 2 dashboard visits)
 */
const PushConsentModal = ({ isOpen, onClose, onConsent }) => {
  if (!isOpen) return null;

  const handleYes = () => {
    onConsent(true);
  };

  const handleNo = () => {
    onConsent(false);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-consent-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-gray-400" />
        </button>
        
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-violet-100 rounded-full">
            <Bell className="h-8 w-8 text-violet-600" />
          </div>
        </div>
        
        {/* Title */}
        <h2 
          id="push-consent-title"
          className="text-xl font-semibold text-center text-gray-900 mb-2"
        >
          Stay Updated on Your Learning
        </h2>
        
        {/* Description */}
        <p className="text-gray-600 text-center mb-6">
          Would you like reminders about live sessions, deadlines, and new content?
        </p>
        
        {/* Benefits list */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-sm text-gray-700">
            <Calendar className="h-5 w-5 text-violet-500 flex-shrink-0" />
            <span>Deadline reminders so you never miss submissions</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-700">
            <BookOpen className="h-5 w-5 text-violet-500 flex-shrink-0" />
            <span>New content alerts when courses are updated</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-700">
            <Clock className="h-5 w-5 text-violet-500 flex-shrink-0" />
            <span>Live session notifications before they start</span>
          </div>
        </div>
        
        {/* Privacy note */}
        <p className="text-xs text-gray-500 text-center mb-6">
          You can unsubscribe anytime from your profile settings. 
          We respect your privacy and will only send relevant learning updates.
        </p>
        
        {/* Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleNo}
            className="flex-1"
            data-testid="push-consent-no"
          >
            No, thanks
          </Button>
          <Button
            onClick={handleYes}
            className="flex-1 bg-violet-600 hover:bg-violet-700"
            data-testid="push-consent-yes"
          >
            Yes, notify me
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PushConsentModal;
